import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Ban, Check, Loader2, ShieldCheck, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../context/LanguageContext";
import {
  getAdminUsers,
  approveUser,
  blockUser,
  deleteUser,
  getCompanySettings,
  updateCompanyEmail,
} from "../../lib/metalApi";

const EASE = [0.16, 1, 0.3, 1];

const Card = ({ title, children, testId }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, ease: EASE }}
    data-testid={testId}
    className="rounded-2xl border border-[#342C55] bg-[#221D3F] p-5"
  >
    <h3 className="mb-4 font-manrope text-xs font-bold uppercase tracking-[0.16em] text-[#8B87A8]">
      {title}
    </h3>
    {children}
  </motion.section>
);

export const AdminTab = ({ currentUser }) => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const load = async () => {
    setError(false);
    try {
      const u = await getAdminUsers();
      setUsers(Array.isArray(u) ? u : u.items ?? []);
    } catch {
      setError(true);
    }
    try {
      const s = await getCompanySettings();
      setSettings(s);
      setEmail(s?.email_classifiche || "");
    } catch {
      setSettings(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (fn, id) => {
    try {
      await fn(id);
      await load();
    } catch {
      toast.error(t.reserved.errorGeneric);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.reserved.confirmDelete)) return;
    await act(deleteUser, id);
  };

  const onSaveEmail = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCompanyEmail(email.trim());
      toast.success(t.reserved.saved);
      await load();
    } catch {
      toast.error(t.reserved.errorGeneric);
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <p className="rounded-2xl border border-[#34D399]/40 bg-[#34D399]/10 px-4 py-3 font-manrope text-sm text-[#34D399]">
        {t.reserved.dataError}
      </p>
    );
  }

  const pending = users.filter((u) => !u.is_approved);
  const active = users.filter((u) => u.is_approved);

  return (
    <div className="space-y-5" data-testid="admin-tab">
      <Card title={t.reserved.pending} testId="admin-pending">
        {pending.length === 0 ? (
          <p className="font-manrope text-sm text-[#8B87A8]">{t.reserved.noPending}</p>
        ) : (
          <div className="space-y-3">
            {pending.map((u, i) => (
              <div
                key={u.id ?? i}
                data-testid={`admin-pending-${i}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-amber-400/30 bg-amber-400/5 px-4 py-3"
              >
                <span className="font-manrope text-sm font-semibold text-[#F5F5F5]">{u.username}</span>
                <button
                  data-testid={`admin-approve-${i}`}
                  onClick={() => act(approveUser, u.id)}
                  className="flex items-center gap-2 rounded-xl bg-[#34D399] px-4 py-2 font-manrope text-xs font-bold text-[#17122E] transition-colors duration-300 hover:bg-[#2BBD8C]"
                >
                  <UserCheck className="h-4 w-4" />
                  {t.reserved.approve}
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={t.reserved.allUsers} testId="admin-users">
        <div className="space-y-2">
          {active.map((u, i) => (
            <div
              key={u.id ?? i}
              data-testid={`admin-user-${i}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-[#342C55] px-4 py-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#34D399]/15">
                  <ShieldCheck className="h-4 w-4 text-[#34D399]" />
                </span>
                <span className="truncate font-manrope text-sm text-[#F5F5F5]">{u.username}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-[#34D399]/15 px-2.5 py-0.5 font-manrope text-[10px] font-bold uppercase tracking-wide text-[#34D399]">
                  {u.role === "super_admin" ? "ADMIN" : u.role}
                </span>
                {u.id !== currentUser?.id && (
                  <>
                    <button
                      data-testid={`admin-block-${i}`}
                      onClick={() => act(blockUser, u.id)}
                      aria-label={t.reserved.block}
                      className="rounded-lg p-1.5 text-[#8B87A8] transition-colors duration-300 hover:bg-amber-400/10 hover:text-amber-300"
                    >
                      <Ban className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`admin-delete-${i}`}
                      onClick={() => onDelete(u.id)}
                      aria-label={t.reserved.delete}
                      className="rounded-lg p-1.5 text-[#8B87A8] transition-colors duration-300 hover:bg-[#F87171]/10 hover:text-[#F87171]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {settings && (
        <Card title={t.reserved.companySettings} testId="admin-settings">
          <p className="mb-4 font-manrope text-xs text-[#8B87A8]">
            {t.reserved.companyCode}: <span className="font-bold text-[#34D399]">{settings.code}</span>
          </p>
          <form onSubmit={onSaveEmail} className="flex flex-col gap-3 sm:flex-row">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.reserved.emailClassifiche}
              className="h-12 flex-1 rounded-2xl border border-[#342C55] bg-[#17122E] px-4 font-manrope text-sm text-[#F5F5F5] placeholder:text-[#8B87A8] focus:border-[#34D399] focus:outline-none"
            />
            <button
              data-testid="admin-save-button"
              type="submit"
              disabled={saving}
              className="flex h-12 items-center justify-center gap-2 rounded-2xl bg-[#34D399] px-6 font-manrope text-sm font-bold text-[#17122E] transition-colors duration-300 hover:bg-[#2BBD8C] disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {t.reserved.save}
            </button>
          </form>
        </Card>
      )}
    </div>
  );
};
